import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Grid, Typography } from '@material-ui/core'
import { RootState } from 'src/store/ducks/rootReducer'
import { postsRequest } from 'src/store/ducks/news/actions'
import { categoriesRequest } from 'src/store/ducks/categories/actions'
import sortByDate from 'src/utils/sortByDate'
import usePersistedState from 'src/hooks/usePersistedState'
import BreadcrumbsComponent from 'src/components/BreadcrumbsComponent/BreadcrumbsComponent'
import ExpandedNewsItemCard from 'src/components/ExpandedNewsItemCard/ExpandedNewsItemCard'
import ExpandedSkeletonNewsCard from 'src/components/Skeleton/News/ExpandedSkeletonNewsCard/ExpandedSkeletonNewsCard'
import { PUBLISHED_STATUS } from 'src/components/NewsModule/constants'
import SearchInput from 'src/components/SearchInput/SearchInput'
import getCategoryRoute from 'src/utils/News/getCategoryRouteByCategoryId'
import EmptyState from 'src/components/EmptyState/EmptyState'
import { useGlobalStyles } from 'src/mui/styles/globalStyles'
import { useStyles } from './SearchNewsContent.styles'

const SearchNewsContent = () => {
  const [loading, setLoading] = useState(true)
  const history = useHistory()
  const [user] = usePersistedState('auth-token', '')
  const { t } = useTranslation()

  const dispatch = useDispatch()
  const { news } = useSelector((state: RootState) => state.news)
  const { categories } = useSelector((state: RootState) => state.categories)

  const categoryCareer = categories?.find((e) => e.title === 'Vaga')
  const [postList, setPostList] = useState<any>([])

  const classesGlobal = useGlobalStyles()
  const classes = useStyles()

  const text = useMemo(() => {
    const searchParam = new URLSearchParams(location.search).get('q') || ''
    return decodeURIComponent(searchParam).trim()
  }, [location.search])

  useEffect(() => {
    dispatch(postsRequest())
    dispatch(categoriesRequest())
  }, [])

  if (!text) {
    history.push('/notFound')
  }

  useEffect(() => {
    if (!news || !categoryCareer || !news.length) return

    if (!text) {
      setPostList([])
      setLoading(false)
      return
    }

    const filteredNews = sortByDate(news, PUBLISHED_STATUS).filter((post) => {
      const matchesStatus = post.status === PUBLISHED_STATUS
      const matchesCategory = post.newsCategoriesId !== categoryCareer.id
      const matchesAudience = user ? true : post.audience === 'Externo'
      const matchesSearch =
        post.title.toLowerCase().includes(text.toLowerCase()) ||
        post.text.toLowerCase().includes(text.toLowerCase())

      return (
        matchesStatus && matchesCategory && matchesAudience && matchesSearch
      )
    })

    setPostList(filteredNews)
    setLoading(false)
  }, [news, categoryCareer, text, user])

  return (
    <>
      <Box className={classesGlobal.containerPages}>
        <Box className={classesGlobal.topPageBreadcrumbsSearch}>
          <BreadcrumbsComponent
            crumbs={[
              { label: t('navbar.start'), path: '/' },
              { label: t('news.title'), path: '/news' },
              { label: t('news.search-result') }
            ]}
          />
          <SearchInput
            redirectUrl="/news/search"
            placeholder={t('news.placeholder-search')}
            initialValue={text}
          />
        </Box>
        <Typography variant="body1" className={classes.title}>
          {loading
            ? `${t('news.load')} ${t('news.results-for')}: ${text}`
            : `${t('news.showing')} ${postList.length} ${t(
                'news.results-for'
              )}: ${text}`}
        </Typography>
        {loading ? (
          <Grid container>
            {[...Array(3)].map((_, i) => (
              <Grid item xs={12} sm={6} md={6} lg={4} key={i}>
                <ExpandedSkeletonNewsCard />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box width="100%">
            {postList.length > 0 ? (
              <Grid container>
                {postList.map((post: any) => {
                  return (
                    <Grid item xs={12} sm={6} md={6} lg={4} key={post.id}>
                      <ExpandedNewsItemCard
                        idNews={post.id}
                        image={post.path}
                        category={getCategoryRoute(
                          post.newsCategoriesId,
                          categories
                        )}
                        title={post.title}
                        text={post.text}
                      />
                    </Grid>
                  )
                })}
              </Grid>
            ) : (
              <EmptyState searchTerm={text} />
            )}
          </Box>
        )}
      </Box>
    </>
  )
}

export default SearchNewsContent
